// Export search results functionality - 105 lines ✅
// @requirement SPEC-KB-SEARCH-001/FR-09: Export search results

import { Download, FileText, FileSpreadsheet } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { SearchResult } from '@/types/knowledge'

interface ExportResultsProps {
  results: SearchResult[]
  query: string
  disabled?: boolean
}

export function ExportResults({
  results,
  query,
  disabled = false,
}: ExportResultsProps): JSX.Element {
  const exportToJSON = (): void => {
    const data = {
      query,
      exportedAt: new Date().toISOString(),
      totalResults: results.length,
      results: results.map((r) => ({
        id: r.issue.id,
        title: r.issue.title,
        description: r.issue.description,
        similarity: r.similarity,
        compositeScore: r.compositeScore,
        d365Versions: r.issue.d365Versions,
        d365Modules: r.issue.d365Modules,
        successRate: r.issue.successRate,
        isValidated: r.issue.isValidated,
        resolutionCount: r.resolutionMethods.length,
        createdAt: r.issue.createdAt,
      })),
    }

    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'application/json',
    })
    downloadFile(blob, `kb-search-${formatFilename(query)}.json`)
  }

  const exportToCSV = (): void => {
    const headers = [
      'ID',
      'Title',
      'Description',
      'Similarity %',
      'Composite Score %',
      'D365 Versions',
      'Modules',
      'Success Rate %',
      'Validated',
      'Resolution Count',
      'Created At',
    ]

    const rows = results.map((r) => [
      r.issue.id,
      `"${r.issue.title.replace(/"/g, '""')}"`,
      `"${r.issue.description.replace(/"/g, '""')}"`,
      Math.round(r.similarity * 100),
      Math.round(r.compositeScore * 100),
      `"${r.issue.d365Versions.join(', ')}"`,
      `"${r.issue.d365Modules.join(', ')}"`,
      r.issue.successRate ? Math.round(r.issue.successRate * 100) : '',
      r.issue.isValidated ? 'Yes' : 'No',
      r.resolutionMethods.length,
      new Date(r.issue.createdAt).toISOString(),
    ])

    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n')

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    downloadFile(blob, `kb-search-${formatFilename(query)}.csv`)
  }

  const downloadFile = (blob: Blob, filename: string): void => {
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const formatFilename = (query: string): string => {
    const sanitized = query
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 30)
    const timestamp = new Date().toISOString().split('T')[0]
    return `${sanitized}-${timestamp}`
  }

  if (results.length === 0 || disabled) {
    return (
      <Button
        variant="outline"
        size="sm"
        disabled
        className="border-kaycoo-charcoal-2 text-kaycoo-charcoal-2"
      >
        <Download className="h-4 w-4 mr-2" />
        Export
      </Button>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="border-kaycoo-teal-dark text-kaycoo-teal-dark hover:bg-kaycoo-teal-pale"
        >
          <Download className="h-4 w-4 mr-2" />
          Export ({results.length})
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem onClick={exportToJSON} className="cursor-pointer">
          <FileText className="h-4 w-4 mr-2" />
          Export as JSON
        </DropdownMenuItem>
        <DropdownMenuItem onClick={exportToCSV} className="cursor-pointer">
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          Export as CSV
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
